import { Component, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LabelComponent } from '../../form/label/label.component';
import { InputFieldComponent } from '../../form/input/input-field.component';
import { AlertComponent } from '../../ui/alert/alert.component';
import { AccessRequestApiService } from '../../../../pages/auth-pages/services/access-request-api.service';
import { InvitationApiService } from '../../../../pages/invitations/services/invitation-api.service';
import { ToastService } from '../../../services/toast.service';

type SignupMode = 'invitation' | 'request';

/**
 * Pantalla `/signup`.
 *
 * <p><b>Partner CRM no tiene registro de auto-servicio.</b> Es un producto de pago
 * que se vende de forma asistida, así que no existe (ni debe existir) una forma de
 * que un visitante se cree una cuenta o una empresa solo. Esta pantalla ofrece los
 * dos caminos que sí son reales:</p>
 *
 * <ol>
 *   <li><b>Canjear una invitación</b> — el usuario pega el código que recibió por
 *       correo. Se valida contra <code>GET /api/invitations/token/{token}</code> antes
 *       de navegar, para que un código malo dé un mensaje claro aquí y no un error
 *       en la pantalla siguiente.</li>
 *   <li><b>Solicitar acceso</b> — deja sus datos y el equipo comercial lo contacta.
 *       <code>POST /api/public/access-requests</code>; no crea nada en el CRM.</li>
 * </ol>
 *
 * <p>Antes esta pantalla era la plantilla de TailAdmin sin tocar: el formulario no
 * enviaba nada (<code>onSignIn()</code> hacía <code>console.log</code>) y el botón
 * "Sign up with Google" no tenía handler. La landing enlaza aquí desde cinco CTA.</p>
 */
@Component({
  selector: 'app-signup-form',
  imports: [
    LabelComponent,
    InputFieldComponent,
    AlertComponent,
    RouterModule,
    FormsModule,
  ],
  templateUrl: './signup-form.component.html',
  styles: ``,
})
export class SignupFormComponent {
  private readonly accessRequestApi = inject(AccessRequestApiService);
  private readonly invitationApi = inject(InvitationApiService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  mode: SignupMode = 'request';

  // --- Solicitar acceso ---
  fullname = '';
  companyName = '';
  email = '';
  phone = '';
  message = '';
  /** Honeypot: oculto en el template, una persona nunca lo rellena. */
  website = '';

  isSubmitting = false;
  submitted = false;
  errorMessage = '';

  // --- Canjear invitación ---
  invitationCode = '';
  isCheckingCode = false;
  codeError = '';

  setMode(mode: SignupMode): void {
    this.mode = mode;
    this.errorMessage = '';
    this.codeError = '';
  }

  get canSubmitRequest(): boolean {
    return (
      !this.isSubmitting &&
      this.fullname.trim().length > 0 &&
      this.companyName.trim().length > 0 &&
      this.isEmailValid
    );
  }

  get isEmailValid(): boolean {
    const value = this.email.trim();
    return value.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  submitRequest(): void {
    if (!this.canSubmitRequest) {
      return;
    }
    this.isSubmitting = true;
    this.errorMessage = '';

    this.accessRequestApi
      .submit({
        fullname: this.fullname.trim(),
        companyName: this.companyName.trim(),
        email: this.email.trim(),
        phone: this.phone.trim() || undefined,
        message: this.message.trim() || undefined,
        website: this.website,
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.submitted = true;
          this.toast.success('Solicitud enviada. Te contactaremos pronto.');
        },
        error: (err) => {
          this.isSubmitting = false;
          this.errorMessage =
            err?.error?.message ||
            'No pudimos enviar tu solicitud. Inténtalo nuevamente en unos minutos.';
        },
      });
  }

  /**
   * El token de invitación son 64 caracteres hexadecimales y viaja dentro del
   * enlace del correo. Se acepta que el usuario pegue el enlace completo y no
   * sólo el código: se extrae el último segmento.
   */
  redeemCode(): void {
    const token = this.extractToken(this.invitationCode);
    if (!token) {
      this.codeError = 'Pega el código o el enlace completo que recibiste por correo.';
      return;
    }

    this.isCheckingCode = true;
    this.codeError = '';

    this.invitationApi.getInvitationByToken(token).subscribe({
      next: () => {
        this.isCheckingCode = false;
        this.router.navigate(['/invite', token]);
      },
      error: (err) => {
        this.isCheckingCode = false;
        this.codeError =
          err?.status === 404
            ? 'No encontramos esa invitación. Revisa el código o pide que te la reenvíen.'
            : err?.error?.message ||
              'No pudimos validar la invitación. Inténtalo nuevamente.';
      },
    });
  }

  private extractToken(raw: string): string | null {
    const value = (raw || '').trim();
    if (!value) {
      return null;
    }
    // Acepta el enlace completo (…/#/invite/<token>) o el código suelto.
    const segments = value.split(/[/#?&=]+/).filter((s) => s.length > 0);
    const candidate = segments.length > 0 ? segments[segments.length - 1] : value;
    return /^[A-Za-z0-9]{16,}$/.test(candidate) ? candidate : null;
  }
}
